import { atom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import type { ListSkillsQuery } from "~/api/API";

/**
 * Whether the Indy chat panel is open. Backed by `sessionStorage` (per-tab),
 * NOT `localStorage`: a user may run Indy on appointment A in one tab and
 * appointment B in another, and these must be independent. `localStorage` is
 * shared browser-wide and its default jotai storage subscribes to the window
 * `storage` event, so opening/closing Indy in one tab would be broadcast to
 * every other tab ("forced movement across tabs"). `sessionStorage` is scoped
 * to a single tab and still survives reloads within that tab.
 */
export const chatbotOpenAtom = atomWithStorage<boolean>(
  "chatbotOpen",
  false,
  createJSONStorage<boolean>(() => sessionStorage),
  { getOnInit: true },
);

export const chatbotLastConversationIdAtom = atom<string | null>(null);

/** Per-user Auto/Off choice, shared across views and retained on reload. */
export const indyWebSearchPreferencesAtom = atomWithStorage<
  Record<string, boolean>
>(
  "indyWebSearchPreferences",
  {},
  createJSONStorage<Record<string, boolean>>(() => localStorage),
  { getOnInit: true },
);

/**
 * The hand-drawn glasses variant this user picked for their Indy launcher.
 * Cosmetic per-user preference, so it lives in `localStorage` (browser-wide,
 * survives reloads, shared across this browser's tabs) — no backend/schema.
 * The value is a variant id from `indyGlasses.ts`; unknown ids fall back to the
 * default at read time via `glassesById`. Default "original" is inlined here to
 * keep the atoms layer free of component/asset imports.
 */
export const indyGlassesVariantAtom = atomWithStorage<string>(
  "indyGlasses",
  "original",
  createJSONStorage<string>(() => localStorage),
  { getOnInit: true },
);

/** Viewport pixels of the launcher's top-left corner. */
export interface LauncherPosition {
  x: number;
  y: number;
}

/**
 * Where the user parked the Indy launcher, in viewport pixels of the button's
 * top-left corner. `null` = never moved, so the CSS default (bottom-right)
 * stands.
 *
 * In memory, not `localStorage`: moving the button is a nudge to get it off
 * whatever it is covering right now — the page-number controls on the medical
 * record editor being the case that prompted the drag — and not a preference
 * the way `indyGlassesVariantAtom` above is. A refresh clears it, the one
 * boundary it shares with the chat panel's own width and height
 * (`ChatbotModal.tsx`); geometry surviving a reload here but not there was the
 * inconsistency. Only that boundary, though — this atom lives in the store, so
 * it also outlives closing the panel and a trip through `/indy`, where
 * `ChatbotModal` unmounts and its `useState` sizes go with it.
 *
 * The trade is that a user who drags the button every session drags it every
 * session. Reloading with Indy already open is the sharper case:
 * `chatbotOpenAtom` above is `sessionStorage`-backed, so the panel remounts
 * immediately with this atom back at `null` and re-anchors to the default
 * corner — over whatever the drag was meant to clear. The launcher is hidden
 * while the panel is open (`MainLayout.tsx`), so the panel's own title bar is
 * what moves it off again.
 *
 * Going back to `atomWithStorage` is two lines but is not clean: its `onMount`
 * re-reads storage whatever `getOnInit` says, so the key existing users still
 * carry would resurrect a spot parked months ago on another monitor — clear
 * `indyLauncherPosition` first. The panel's size would want persisting
 * alongside it, both with a stored-value clamp.
 *
 * Coordinates are clamped into the viewport on read (`indyLauncherPosition.ts`)
 * and again on window resize (`ChatbotLauncher.tsx`), which a live drag on a
 * shrinking window needs regardless of where the value is kept.
 */
export const indyLauncherPositionAtom = atom<LauncherPosition | null>(null);

/**
 * One-shot signal: when set, the next ChatbotModal open transition seeds the
 * input with `/<skillName> ` and clears the atom. Used by the Skills editor's
 * "Test invoke" button to drop the author straight into a chat with the
 * slash command pre-filled — reuses the Slice A slash autocomplete instead
 * of introducing an isolated test-invoke surface.
 */
export const chatbotSlashPrefillAtom = atom<string | null>(null);

/**
 * Skill row shape as returned by `listSkills` — derived from the codegened
 * `ListSkillsQuery` so the cache + downstream consumers stay in lockstep
 * with the actual GraphQL selection set. Avoids the `as unknown as Skill[]`
 * cast that would otherwise be needed to bridge the inline anonymous type
 * vs the named `Skill` type (which has a different `inputs` shape).
 */
export type SkillMenuItem = NonNullable<
  NonNullable<ListSkillsQuery["listSkills"]>["items"][number]
>;

/**
 * Cache of skills available to the current user, populated on chat-modal open
 * via `listSkills`. Null = never fetched. Read+filtered by `useAvailableSkills`.
 * Refresh on each modal open if `fetchedAt` is older than the hook's TTL.
 */
export interface AvailableSkillsCache {
  skills: SkillMenuItem[];
  fetchedAt: number;
}

export const availableSkillsAtom = atom<AvailableSkillsCache | null>(null);
